import { likedFolderService, likeGoodsService } from '../../../api';
import { bridge, delay, isIOS, isWeb } from '../../../utils';
import { isAndroid } from '../../../utils/isAndroid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { Dimensions, Keyboard, TextInput } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { queryKey } from '../../../constant';
import { useSafeTimeout } from '../../useSafeTimeout';

import {
  BottomSheetContainer,
  ColorValues,
  createUseBottomSheetModal,
  Flex,
  KeyboardAvoiding,
  PrimaryButton,
  Typography,
  useBottomSheetEntered,
} from '@banhala/ui-kit';
import Toast from 'react-native-toast-message';
import { toast } from '../toast';
import { useOpenDeeplink } from '../../useOpenDeeplink';

type UseCreateNewLikeFolderBottomSheet = {
  goodsSno: number;
  listParams?: string;
  screen: string;
};
export const useCreateNewLikeFolderBottomSheet = createUseBottomSheetModal<
  { goodsSno: number; folderName: string; folderSno: number },
  UseCreateNewLikeFolderBottomSheet
>(({ confirm, cancel, param: { goodsSno, listParams, screen } }) => {
  const { bottom, top } = useSafeAreaInsets();
  const [title, setTitle] = useState('');
  const ref = useRef<TextInput>(null);
  useFocusOnBottomSheetEntered(ref);
  const bottomSheetMinHeightInNative =
    (Dimensions.get('window').height - bottom - top) / 3;
  const bottomSheetMinHeightInWeb = useRef(Dimensions.get('window').height / 3);

  const { mutateAsync: mutateAsyncCreateLikeFolder } = useMutation({
    mutationFn: likedFolderService.post,
    throwOnError: false,
  });
  const { mutateAsync: mutateAsyncLikeGoods } = useMutation({
    mutationFn: likeGoodsService.post,
    throwOnError: false,
  });

  const queryClient = useQueryClient();
  const { openDeeplink } = useOpenDeeplink();

  return (
    <KeyboardAvoiding>
      <BottomSheetContainer
        onPressClose={() => {
          Keyboard.dismiss();
          cancel();
        }}
        minHeight={
          isWeb
            ? bottomSheetMinHeightInWeb.current
            : isIOS
              ? bottomSheetMinHeightInNative
              : 0
        }
        title="새 서랍 만들기"
      >
        <Flex.Column
          position={'relative'}
          flex={isAndroid ? undefined : 1}
          padding={{ vertical: 16 }}
        >
          <Flex.Column
            padding={{ horizontal: 10 }}
            height={isAndroid ? 78 : undefined}
          >
            <TextInput
              ref={ref}
              style={{
                fontSize: 16,
                outlineWidth: 0,
              }}
              placeholder="서랍 이름은 최소 한 글자 이상 입력해주세요."
              placeholderTextColor={ColorValues.gray50}
              autoFocus={!isWeb}
              multiline
              numberOfLines={2}
              onChangeText={value => {
                setTitle(value);
              }}
              maxLength={30}
            />
            <Flex.Row position={'absolute'} right={10} top={60}>
              <Typography
                color={'content_secondary'}
              >{`${title.length}`}</Typography>
              <Typography color={'content_disabled'}>{` / 30`}</Typography>
            </Flex.Row>
          </Flex.Column>
        </Flex.Column>
        <PrimaryButton
          variant={'fill'}
          color={'pink'}
          removeBorderRadius={true}
          // disabled={title.length === 0}
          state={title.length === 0 ? 'disabled' : undefined}
          onPress={async () => {
            try {
              const {
                data: { sno },
              } = await mutateAsyncCreateLikeFolder({
                title,
              });
              await mutateAsyncLikeGoods({
                list_params_referrer: listParams,
                sno: goodsSno,
                folder_sno: sno,
              });
              toast({
                folderName: title,
                folderSno: sno,
                screen,
                openDeeplink,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.getInfiniteLikeFolders,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.getInfiniteRecentlyViewedGoods,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.getMemberInfoQuery,
              });

              bridge.likeGoods({
                goods_sno: goodsSno,
                is_liked: true,
                folder_sno: sno,
              });
              confirm({ goodsSno, folderName: title, folderSno: sno });
            } catch (error) {
              cancel();
              await delay(500);
              const errorMessage =
                error instanceof Error && error.message
                  ? error.message
                  : '오류가 발생했어요';
              Toast.show({
                type: 'success',
                text1: errorMessage,
                position: 'bottom',
              });
            }
          }}
        >
          완료
        </PrimaryButton>
      </BottomSheetContainer>
    </KeyboardAvoiding>
  );
});

/**
 * BottomSheet entering 애니메이션 종료 시점에 TextInput에 focus.
 *
 * 배경: Android에서 mount 직후 `ref.focus()` 호출하면 view가 window에 fully attach되기 전이라
 * `IMM.showSoftInput()`이 silently fail해서 키보드가 안 뜨는 race가 있음 (EN-2761/2753).
 *
 * `useBottomSheetEntered`는 SHOW_DURATION(300ms) 애니메이션 종료 후 true를 반환하므로,
 * 그 시점에 focus를 호출하면 view attach + layout pass 완료 후라 race 확률 크게 감소.
 * 추가 50ms 안전 마진을 둠.
 */
const useFocusOnBottomSheetEntered = (ref: RefObject<TextInput | null>) => {
  const entered = useBottomSheetEntered();
  const setSafeTimeout = useSafeTimeout();
  useEffect(() => {
    if (!entered || !isAndroid) return;
    setSafeTimeout(() => {
      ref.current?.blur();
      ref.current?.focus();
    }, 50);
  }, [entered, ref, setSafeTimeout]);
};
